import type { AroundRadiusAll } from '@algolia/client-search';

import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import type { ApiJobFunction } from '#/api/types/ApiJobFunction';
import type { ApiSynonymMini } from '#/api/types/ApiSynonymMini';
import type { AppConfig } from '#/config/types/AppConfig';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import type { SearchAlgoliaJobHit } from '#/modules/search/algolia/types/SearchAlgoliaJobHit';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';

const ATTRIBUTES: (keyof SearchAlgoliaJobHit)[] = [
  'objectID',
  'type',
  'published',
  'name',
  'city',
  'state',
  'stateStr',
  'country',
  'url',
  'orgID',
  'orgUrl',
  'orgName',
  'orgType',
  'jobType',
  'logo',
  'locationType',
  'remoteZone',
  'hasAts',
  'isPostedAnonymously',
  'salaryMinimum',
  'salaryMaximum',
  'salaryCurrency',
  'salaryPeriod',
];

export async function searchAlgoliaFetchSimilarJobs(
  jobTitle: string,
  synonym: ApiSynonymMini | null,
  functions: ApiJobFunction[],
  userSearchLocation: SearchLocation | undefined,
  orgAreasOfFocus: ApiAreaOfFocus[] | undefined,
  appConfig: AppConfig,
) {
  const filterArray = [];

  if (synonym?.id) {
    filterArray.push(`compdb.synonymId:${synonym.id}<score=100000>`);
    filterArray.push(`compdb.jobTitleId:${synonym.jobTitleId}<score=10000>`);

    // @NOTE: Algolia does not allow (A AND B) OR (C AND D) OR (E AND F)
    // filtering, so we work around by concatenating familyId AND level when indexing
    // the jobs in Algolia to make A-B OR C-D OR E-F
    // @SEE: https://www.algolia.com/doc/guides/managing-results/refine-results/filtering/in-depth/combining-boolean-operators/#combine-ands-and-ors
    filterArray.push(
      `familyIdWithJobLevel:${synonym.familyId}-${
        synonym.level - 1
      }<score=1000> OR familyIdWithJobLevel:${synonym.familyId}-${
        synonym.level
      }<score=1000> OR familyIdWithJobLevel:${synonym.familyId}-${
        synonym.level + 1
      }<score=1000>`,
    );
  }

  if (functions && functions.length > 0) {
    filterArray.push(
      functions
        .map((f: ApiJobFunction) => `functions:${f}<score=10>`)
        .join(' OR '),
    );
  }

  if (orgAreasOfFocus && orgAreasOfFocus.length > 0) {
    filterArray.push(
      orgAreasOfFocus
        .map(
          (areaOfFocus: ApiAreaOfFocus) =>
            `areasOfFocus:${areaOfFocus}<score=1>`,
        )
        .join(' OR '),
    );
  }

  const filters =
    filterArray.length > 1
      ? `(${filterArray.join(' OR ')}) AND type:JOB`
      : `${filterArray[0]} AND type:JOB`;

  // Optional filters are applied if present, but do not exclude results
  // that do not have them - this allows us to boost remote listings
  // over listings that are further away, but have a high filter score
  const optionalFilters = ['locationType:REMOTE'];

  let aroundLatLng;
  if (userSearchLocation?.latitude && userSearchLocation.longitude) {
    aroundLatLng = `${userSearchLocation.latitude}, ${userSearchLocation.longitude}`;
  }

  // No filtering on radius
  const aroundRadius: AroundRadiusAll = 'all';

  const options = {
    aroundLatLng,
    aroundRadius,
    hitsPerPage: 20,
    attributesToRetrieve: ATTRIBUTES as string[],
    removeWordsIfNoResults: 'firstWords' as const,
    sumOrFiltersScores: true,
    facets: ['*'],
    filters,
    optionalFilters,
  };

  const searchConfig = getSearchAlgoliaDefaultConfig({ appConfig });
  let jobRecommendationsResult = await algoliaSearch<SearchAlgoliaJobHit>({
    config: searchConfig,
    indexName: appConfig.algolia.siteRecommendationsIndexName,
    query: '',
    options,
    isUsingAlgoliaMock: appConfig.algolia.mock,
  });

  if (jobRecommendationsResult.nbHits === 0) {
    jobRecommendationsResult = await algoliaSearch<SearchAlgoliaJobHit>({
      config: searchConfig,
      query: synonym ? synonym.name : jobTitle,
      indexName: appConfig.algolia.sitePublishedDescIndexName,
      options: {
        ...options,
        filters: 'type:JOB',
        optionalFilters: undefined,
      },
      isUsingAlgoliaMock: appConfig.algolia.mock,
    });
  }

  return jobRecommendationsResult;
}
